import { useEffect, useState } from "react";

import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import {
  linkableProviders,
  myIdentities,
  startSsoLink,
  unlinkIdentity,
  type MyIdentity,
  type PublicProvider,
} from "@/api/sso";

const UNLINK_MESSAGES: Record<string, string> = {
  would_lock_account_out:
    "C'est votre seul moyen de connexion : définissez d'abord un mot de passe.",
};

/**
 * Let the account holder move themselves onto SSO.
 *
 * The point is the transition: an existing password account binds its Google
 * (or Entra) identity here, and from the next visit the SSO button on the login
 * screen opens the same account. Nothing is provisioned — the account already
 * exists, which is the whole reason this is safe.
 */
export function SsoLinkPanel() {
  const [identities, setIdentities] = useState<MyIdentity[]>([]);
  const [available, setAvailable] = useState<PublicProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    Promise.all([myIdentities(), linkableProviders()])
      .then(([ids, avail]) => {
        setIdentities(ids);
        setAvailable(avail);
      })
      .catch(() => setError("Impossible de charger les identités associées."))
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const link = async (p: PublicProvider) => {
    setBusy(p.id);
    setError(null);
    try {
      const { authorization_url } = await startSsoLink(p.id);
      window.location.assign(authorization_url);
    } catch {
      setError("Impossible de démarrer l'association.");
      setBusy(null);
    }
  };

  const unlink = async (i: MyIdentity) => {
    setBusy(i.id);
    setError(null);
    try {
      await unlinkIdentity(i.id);
      reload();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      const key = typeof detail === "string" ? detail : "";
      setError(UNLINK_MESSAGES[key] ?? "La dissociation a échoué.");
    } finally {
      setBusy(null);
    }
  };

  // Nothing configured for this tenant — say nothing rather than show an empty box.
  if (!loading && identities.length === 0 && available.length === 0) return null;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Connexion par SSO</Typography>
            <Typography variant="body2" color="text.secondary">
              Associez votre compte professionnel pour vous connecter sans mot de passe.
            </Typography>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {loading && <CircularProgress size={22} />}

          {identities.map((i) => (
            <Stack
              key={i.id}
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={i.provider_display_name} color="primary" />
                <Typography variant="body2">{i.email_at_link ?? "—"}</Typography>
                {i.last_login_at === null && (
                  <Typography variant="caption" color="text.secondary">
                    jamais utilisée
                  </Typography>
                )}
              </Stack>
              <Button
                size="small"
                color="error"
                disabled={busy !== null}
                onClick={() => void unlink(i)}
              >
                Dissocier
              </Button>
            </Stack>
          ))}

          {available.map((p) => (
            <Button
              key={p.id}
              variant="outlined"
              disabled={busy !== null}
              onClick={() => void link(p)}
              sx={{ textTransform: "none", alignSelf: "flex-start" }}
            >
              {busy === p.id ? "Redirection…" : `Associer ${p.display_name}`}
            </Button>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
